import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { IdCard, ShieldCheck, ExternalLink, Fish, Info, ArrowRight } from "lucide-react";

export default function LicensePage() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/license").then((r) => setInfo(r.data));
  }, []);

  if (!info) return null;

  return (
    <div>
      {/* hero banner */}
      <section className="bg-emerald-900 text-white grain-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 lg:py-20">
          <span className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/30 text-amber-200 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
            <ShieldCheck className="w-4 h-4" /> Achat officiel
          </span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 max-w-2xl">
            Obtenez votre carte de pêche officielle
          </h1>
          <p className="text-emerald-100/85 mt-4 max-w-2xl leading-relaxed">
            La carte de pêche est <strong>obligatoire</strong> pour pêcher en eau douce en France.
            Elle finance la protection des milieux aquatiques. Achetez-la en ligne sur le site officiel de la {info.provider}.
          </p>
          <a
            href={info.official_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="license-main-cta"
            className="inline-flex items-center gap-2 mt-7 bg-amber-500 hover:bg-amber-600 text-emerald-950 font-bold py-4 px-8 rounded-full transition-all hover:-translate-y-1 shadow-lg"
          >
            <IdCard className="w-5 h-5" /> Acheter sur cartedepeche.fr <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* license types */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-emerald-950">Quelle carte choisir ?</h2>
        <p className="text-slate-600 mt-2">Sélectionnez la formule adaptée à votre profil de pêcheur.</p>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mt-8">
          {info.types.map((t) => (
            <div
              key={t.name}
              data-testid={`license-type-${t.name}`}
              className="group rounded-2xl bg-white border border-emerald-900/10 p-6 transition-all hover:-translate-y-1 hover:shadow-xl flex flex-col"
            >
              <div className="flex items-start justify-between">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-emerald-50">
                  <IdCard className="w-5 h-5 text-emerald-600" />
                </span>
                <span className="font-mono font-bold text-emerald-800 text-sm bg-emerald-50 px-2.5 py-1 rounded-full">{t.price}</span>
              </div>
              <h3 className="font-display font-bold text-lg text-emerald-950 mt-4">{t.name}</h3>
              <p className="text-sm text-slate-600 mt-1 flex-1">{t.desc}</p>
              <p className="text-xs font-semibold text-emerald-700 mt-3 uppercase tracking-wide">{t.audience}</p>
              <a
                href={info.official_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`license-buy-${t.name}`}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-amber-600 group-hover:gap-2 transition-all"
              >
                Choisir cette carte <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-16">
        <div className="rounded-3xl bg-sky-50 border border-sky-200 p-8">
          <h2 className="font-display text-xl font-bold text-emerald-950 flex items-center gap-2">
            <Info className="w-5 h-5 text-sky-600" /> Comprendre les catégories de cours d'eau
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            {info.categories.map((c) => (
              <div key={c.cat} className="rounded-2xl bg-white p-5 border border-sky-100">
                <p className="flex items-center gap-2 font-display font-bold text-emerald-900">
                  <Fish className="w-4 h-4 text-emerald-600" /> {c.cat}
                </p>
                <p className="text-sm text-slate-600 mt-1.5">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
